import React, {useEffect, useState} from 'react';
import TextField from '@material-ui/core/TextField';
import _, {isEmpty} from "lodash";
import {Button, CircularProgress, FormControl, IconButton, InputAdornment, Link, Tooltip} from "@material-ui/core";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AdminService from "../../services/AdminService";
import useAdminUserSession from "../../../../hooks/useAdminUserSession";
import {formatDateUS, validEmail} from "../../../../helpers/utils";
import EmailValidationService from "../../services/EmailValidationService";
import snackbar from "../../services/CityService";

const EmailTextField = ({clearEmail, afterOnBlur, validationData, value, entityData, dataSet, setEmailValidationSuccess, onFieldError, extraEndAdornment, ...props}) => {
    const {session} = useAdminUserSession();
    const [validation, setValidation] = useState(validationData);
    const [fetching, setFetching] = useState(false);
    const [couldNotValidate, setCouldNotValidate] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [erroredEmail, setErroredEmail] = useState();
    const [flagging, setFlagging] = useState(false);
    const [displayFlagButton, setDisplayFlagButton] = useState(false);
    const [existingEmailCheck, setExistingEmailCheck] = useState('');
    const [displayValidateButton, setDisplayValidateButton] = useState(true);
    const [emailValue, setEmailValue] = useState(value);

    const handleEmailValidationSuccess = (value) => {
        if (setEmailValidationSuccess) {
            setEmailValidationSuccess(value);
        }
    };

    useEffect(() => {
        setValidation(validationData);
    }, [validationData]);

  useEffect(() => {
    setEmailValue(value);
    }, [value]);

    // Report validation errors to parent so the Save button can be disabled
    useEffect(() => {
        if (onFieldError) {
          const hasError = ((validation?.valid === false) || couldNotValidate) && !isEmpty(emailValue);
            onFieldError(props.name, hasError);
        }
    }, [validation, couldNotValidate, emailValue]);

    // Clear error on unmount so stale errors don't block Save
    useEffect(() => {
        return () => {
            if (onFieldError) onFieldError(props.name, false);
        };
    }, []);

    useEffect(() => {
        if (entityData?.entity_id && entityData?.entity_type) {
            AdminService.emailCheckByEntity(session, entityData.entity_type, entityData.entity_id, entityData.email_field)
                .then(response => {
                    setExistingEmailCheck(response.data);
                })
                .catch(error => {
                    console.log('Error fetching email checks:', error.message);
                });
        }
    },  [session, entityData?.entity_id, entityData?.entity_type, entityData?.email_field]);

  const onChange = (event) => {
    const trimmed = event.target.value.trim();
    setEmailValue(trimmed);          // keep local state in sync
    event.target.value = trimmed;
    props.onChange?.(event);

    if (props.blackList && _.isArray(props.blackList)) {
      if (props.blackList.includes(trimmed)) {
        setErrorMessage('This email address is not allowed in this field.')
        setErroredEmail('')
        setValidation({valid: false});
      } else {
        setErrorMessage('');
        setErroredEmail(null);
        setValidation(null);
      }
    }

  }

    const validate = (eventOrValue) => {
        if (!flagging) {
            setDisplayFlagButton(false);
            const value = typeof eventOrValue === "string"
                ? eventOrValue
                : emailValue;

            if (!value || value.trim() === '') {
                setValidation(null);
                setErrorMessage('');
                setCouldNotValidate(false);
                if (typeof eventOrValue !== "string" && afterOnBlur) {
                    afterOnBlur(eventOrValue);
                }
                return;
            }

            if (props.blackList && _.isArray(props.blackList)) {
              if (props.blackList.includes(value)) {
                handleEmailValidationSuccess(false);
                setErrorMessage('');
                setErroredEmail(null);
                setValidation(null);
                return;
              }
            }

            if (validEmail(value)) {
                setFetching(true);
                AdminService.validateEmail(session, value, {email_field: props.name, ...entityData}).then((response) => {
                    if (response.data.valid) {
                        setValidation(response.data);
                        if (response.data.status === 'could_not_validate') {
                            setCouldNotValidate(true);
                            setErrorMessage(response.data.message);
                            handleEmailValidationSuccess(false);
                        } else {
                            setCouldNotValidate(false);
                            setErrorMessage('');
                            handleEmailValidationSuccess(true);
                        }
                    } else {
                        setValidation(response.data);
                        setErrorMessage(response.data.message);
                        setErroredEmail(value);
                        handleEmailValidationSuccess(false);
                        if (response.data.status === 'invalid') {
                            setDisplayFlagButton(true);
                        }
                        onFieldError?.(props.name, false);
                        clearEmail();
                    }
                }).catch((error) => {
                    console.log('error', error.message);
                    handleEmailValidationSuccess(false);
                }).finally(() => {
                    setFetching(false);
                });
            } else {
                setValidation({valid: false});
                handleEmailValidationSuccess(false);
            }

            if (typeof eventOrValue === "string") {
                props.onChange?.({
                    target: { ...{ name: props.name, value: value, type: 'text' } }
                });

            } else if (afterOnBlur) {
                afterOnBlur(eventOrValue);
            }
        }
    }

    const handleFlagIssueChange = () => {
        setFlagging(true)
        AdminService.flagEmailValidation(session, {
            email: erroredEmail,
            data_set: dataSet,
        }).then(() => {
            setDisplayFlagButton(false)
            setFlagging(false)
            window.alert('Thanks! Your request to flag the email will be reviewed shortly.');
        })
        .catch((error) => {
            console.log('error', error.message);
        });
    };

    const handleOverrideValidation = async () => {
        let warnMessage  =`This will override the validation status to valid. Are you sure?`;
        if (window.confirm(warnMessage)) {
            try {
                const response = await EmailValidationService.override(existingEmailCheck.id, session, true)
                if (response.data.status === 'success') {
                    setDisplayValidateButton(false);
                    const newEmailValue = existingEmailCheck.address;
                    setEmailValue(newEmailValue);
                    props.onChange?.({
                        target: { ...{ name: props.name, value: newEmailValue, type: 'text' } }
                    });
                    validate(newEmailValue);
                    handleEmailValidationSuccess(true);

                    await snackbar.show("The email validations has been successfully overriden", "success")
                    }
            } catch (e) {
                console.error(e);
                await snackbar.show("Error when overriding validation", "warning");
                handleEmailValidationSuccess(false);
            }
        }
    }

    const hasError = ((validation?.valid === false) || couldNotValidate) && !isEmpty(emailValue)

    return (
        <FormControl fullWidth>
            <TextField
                {...props}
                value={emailValue}
                onChange={onChange}
                onBlur={validate}
                InputProps={{
                    endAdornment: <InputAdornment position="end">
                        {extraEndAdornment}
                        {
                            fetching ? <>
                                <CircularProgress
                                    color="inherit"
                                    size={20} />
                            </> : <>
                                {
                                    (validation?.valid && !couldNotValidate) ? <>
                                        <Tooltip
                                            title={`Validated at: ${formatDateUS(validation.validated_at)}${validation.response.status ? ` | Status: ${validation.response.status}` : ''}`}
                                        >
                                            <CheckCircleIcon className={validation.response.status === 'catch-all' ? 'orange-icon' : 'green-icon'}/>
                                        </Tooltip>
                                    </> : null
                                }
                            </>
                        }
                    </InputAdornment>
                }}
                error={hasError}
            />
            <span>
                {
                  (!validation?.valid && !isEmpty(errorMessage)) ? (
                      <>
                        {erroredEmail ? `${errorMessage} (${erroredEmail})` : errorMessage}
                        {displayFlagButton && (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    style={{ backgroundColor: '#FFA500', color: 'white' }}
                                    onClick={() => handleFlagIssueChange()}
                                >
                                    Flag Issue
                                </Button>
                        </div>
                        )}
                      </>
                  ) : null
                }
                {
                    couldNotValidate ? errorMessage : null
                }
            </span>
            {(session.isSuperAdmin || session.inHouseResearch || session.isManager || session.isResearcher) &&
                !errorMessage && existingEmailCheck && (existingEmailCheck.override_status === false) && (
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px', fontSize: '0.9rem', color: '#555' }}>
                        <span>Invalid Email: {existingEmailCheck.address}</span>
                        {(!session.isResearcher) &&
                            existingEmailCheck.flag_status === 'flagged' && displayValidateButton && (
                            <Button
                                variant="contained"
                                size="small"
                                style={{
                                    backgroundColor: '#4caf50',
                                    color: 'white',
                                    padding: '2px 6px',
                                    fontSize: '0.75rem',
                                    marginLeft: '16px'
                                }}
                                onClick={handleOverrideValidation}
                            >
                                Validate
                            </Button>
                        )}
                    </div>
            )}
        </FormControl>
    );
}

export default EmailTextField;